/**
 * Empty Diagram State Component
 * Issue #281: Show message when no evidence exists for a diagram
 *
 * Displays a user-friendly message when detection fails and no
 * meaningful evidence can be attached to diagram nodes.
 */

export type DiagramType =
  | 'callGraph'
  | 'systemContext'
  | string;

interface EmptyDiagramStateProps {
  diagramType?: DiagramType;
  className?: string;
}

/**
 * Get human-readable diagram name
 */
function getDiagramName(diagramType: DiagramType): string {
  const names: Record<string, string> = {
    callGraph: 'Call Graph',
    systemContext: 'System Context',
  };
  return names[diagramType] || 'Diagram';
}

/**
 * Get detection patterns explanation for each diagram type
 */
function getDetectionPatterns(diagramType: DiagramType): string[] {
  const patterns: Record<string, string[]> = {
    callGraph: [
      'Function definitions',
      'Function calls and references',
      'Method invocations',
    ],
    systemContext: [
      'External API integrations',
      'Database connections',
      'Third-party service usage',
    ],
  };
  return patterns[diagramType] || ['Code patterns specific to this diagram type'];
}

export function EmptyDiagramState({ diagramType = 'unknown', className = '' }: EmptyDiagramStateProps) {
  const diagramName = getDiagramName(diagramType);
  const patterns = getDetectionPatterns(diagramType);

  return (
    <div className={`flex items-center justify-center h-full w-full bg-slate-50 dark:bg-slate-900 ${className}`}>
      <div className="max-w-md text-center p-8">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-slate-400 dark:text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Main Message */}
        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
          No Evidence Found
        </h3>

        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          No code patterns were detected for the <span className="font-medium">{diagramName}</span> diagram.
        </p>

        {/* Detection Patterns */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-left">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wide">
            Patterns we looked for:
          </p>
          <ul className="space-y-1.5">
            {patterns.map((pattern, index) => (
              <li key={index} className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                <svg
                  className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>{pattern}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Help Text */}
        <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">
          This diagram requires specific code patterns to generate meaningful visualizations.
          If you believe your codebase contains these patterns, they may be using non-standard naming conventions.
        </p>
      </div>
    </div>
  );
}

export default EmptyDiagramState;
